import React, {
  memo, useEffect, useRef, useState,
} from 'react';

import {
  TILE_CSS_H,
  TILE_CSS_W,
  colorForBox,
  colorForLabel,
  loadImageGated,
  tileBoxesKey,
} from '../helpers';
import { resolveAssetUrl } from '../signedAssetCache';
import style from './style.module.scss';
import { useMapView } from './Map';

const PREVIEW = (tile) => tile?.webp || tile?.url || null;
const FULL = (tile) => tile?.url || null;
const DEFAULT_TW = 1280;
const DEFAULT_TH = 720;
const MAX_CANVAS_EDGE = 8192;

const tileKeyOf = (tile) => `${tile.row}-${tile.col}-${tile.index}`;

const sourceCellSize = (imgs, list) => {
  const hit = (list || []).find((tile) => {
    const img = imgs.get(tileKeyOf(tile));
    return img?.naturalWidth > 0 && img?.naturalHeight > 0;
  });
  if (!hit) return { tw: DEFAULT_TW, th: DEFAULT_TH };
  const img = imgs.get(tileKeyOf(hit));
  return { tw: img.naturalWidth, th: img.naturalHeight };
};

/**
 * Batch stroke by label color — one beginPath/stroke per color.
 * Only paints tiles that have decoded images (skip placeholders).
 * Returns box count painted.
 */
const paintDetectionFrames = (ctx, list, cellW, cellH, tileBoxes, selectedLabels, prefix, imgs) => {
  if (!selectedLabels?.size || !tileBoxes) return 0;
  const stroke = Math.max(2, Math.min(cellW, cellH) * 0.006);
  const byColor = new Map();

  list.forEach((tile) => {
    if (imgs && !imgs.has(tileKeyOf(tile))) return;
    const boxes = tileBoxes[tileBoxesKey(tile, prefix)] || [];
    if (!boxes.length) return;
    const dx = (Number(tile.col) || 0) * cellW;
    const dy = (Number(tile.row) || 0) * cellH;
    for (let i = 0; i < boxes.length; i += 1) {
      const box = boxes[i];
      if (box?.BoundingBox && selectedLabels.has(box.label)) {
        const {
          Left = 0, Top = 0, Width = 0, Height = 0,
        } = box.BoundingBox;
        const bw = Width * cellW;
        const bh = Height * cellH;
        if (bw >= 0.5 && bh >= 0.5) {
          const color = colorForBox(box);
          let flat = byColor.get(color);
          if (!flat) {
            flat = [];
            byColor.set(color, flat);
          }
          flat.push(dx + Left * cellW, dy + Top * cellH, bw, bh);
        }
      }
    }
  });

  let n = 0;
  byColor.forEach((flat, color) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = stroke;
    ctx.beginPath();
    for (let i = 0; i < flat.length; i += 4) {
      ctx.rect(flat[i], flat[i + 1], flat[i + 2], flat[i + 3]);
      n += 1;
    }
    ctx.stroke();
  });
  return n;
};

/**
 * Paint seats onto a dedicated layer (Figma-style).
 * Uses mosaic CSS coords (xPx/yPx) → atlas pixels. No image dependency.
 * Toggle = CSS visibility on this canvas — never re-run this for show/hide.
 */
const paintSrsLayer = (ctx, w, h, cellW, cellH, overlay, scanId) => {
  ctx.clearRect(0, 0, w, h);
  const seats = overlay?.seats;
  if (!seats?.length || !(cellW > 0) || !(cellH > 0)) return 0;

  const kx = cellW / TILE_CSS_W;
  const ky = cellH / TILE_CSS_H;
  const stroke = Math.max(1, Math.min(cellW, cellH) * 0.004);

  const byColor = new Map();
  for (let i = 0; i < seats.length; i += 1) {
    const s = seats[i];
    if (!(scanId && s.scanId && s.scanId !== scanId)) {
      const color = colorForLabel(s.section || '?');
      let flat = byColor.get(color);
      if (!flat) {
        flat = [];
        byColor.set(color, flat);
      }
      const side = Math.max(1, (s.sizePx || 8) * kx);
      const cx = (Number(s.xPx) || 0) * kx;
      const cy = (Number(s.yPx) || 0) * ky;
      flat.push(cx - side / 2, cy - side / 2, side, side);
    }
  }

  let n = 0;
  byColor.forEach((flat, color) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = stroke;
    ctx.beginPath();
    for (let i = 0; i < flat.length; i += 4) {
      ctx.rect(flat[i], flat[i + 1], flat[i + 2], flat[i + 3]);
      n += 1;
    }
    ctx.stroke();
  });
  return n;
};

const loadOneTile = async (tile) => {
  const signed = PREVIEW(tile);
  if (!signed) return null;
  try {
    let url = await resolveAssetUrl(signed);
    try {
      return await loadImageGated(url);
    } catch {
      const full = FULL(tile);
      if (full && full !== signed) {
        url = await resolveAssetUrl(full);
        return url ? loadImageGated(url) : null;
      }
      return null;
    }
  } catch {
    return null;
  }
};

/**
 * Two layers: base (tiles/boxes) + SRS (pre-baked seats).
 * SRS toggle is CSS visibility — compositor only, no React paint work.
 */
const MosaicCanvas = ({
  tiles,
  rows,
  cols,
  loading = false,
  tileBoxes = null,
  selectedLabels = null,
  boxKeyPrefix = null,
  /** Clears image cache only when this changes (selected scan/option). */
  scanKey = '',
  viewportLoad = true,
  overlay = null,
}) => {
  const canvasRef = useRef(null);
  const boxesCanvasRef = useRef(null);
  const srsCanvasRef = useRef(null);
  const imagesRef = useRef(new Map());
  const atlasRef = useRef({
    w: 0, h: 0, cellW: 0, cellH: 0,
  });
  const inflightRef = useRef(new Set());
  const genRef = useRef(0);
  const [tick, setTick] = useState(0);
  const [atlasEpoch, setAtlasEpoch] = useState(0);
  const [wanted, setWanted] = useState(() => new Set());
  const { viewportEl } = useMapView();

  const list = tiles || [];
  const r = Number(rows) || 0;
  const c = Number(cols) || 0;
  const scanId = boxKeyPrefix || null;
  const listRef = useRef(list);
  listRef.current = list;

  const ensureAtlasSize = () => {
    let {
      w, h, cellW, cellH,
    } = atlasRef.current;
    if (w > 0 && h > 0) {
      return {
        w, h, cellW, cellH,
      };
    }
    const nativeW = c * DEFAULT_TW;
    const nativeH = r * DEFAULT_TH;
    const fit = Math.min(1, MAX_CANVAS_EDGE / nativeW, MAX_CANVAS_EDGE / nativeH);
    w = Math.max(1, Math.floor(nativeW * fit));
    h = Math.max(1, Math.floor(nativeH * fit));
    cellW = w / c;
    cellH = h / r;
    return {
      w, h, cellW, cellH,
    };
  };

  useEffect(() => {
    genRef.current += 1;
    imagesRef.current = new Map();
    inflightRef.current = new Set();
    atlasRef.current = {
      w: 0, h: 0, cellW: 0, cellH: 0,
    };
    setWanted(new Set());
    setTick((n) => n + 1);
    return () => { genRef.current += 1; };
  }, [scanKey]);

  // Single-region: request every tile. Do not depend on viewportEl (it mounts async
  // and was re-firing this effect, canceling in-flight loads → permanent gaps).
  useEffect(() => {
    if (viewportLoad || loading || !list.length || r < 1 || c < 1) return;
    const all = new Set(list.map(tileKeyOf));
    setWanted((prev) => {
      if (prev.size === all.size) {
        let same = true;
        all.forEach((k) => { if (!prev.has(k)) same = false; });
        if (same) return prev;
      }
      return all;
    });
  }, [viewportLoad, loading, scanKey, list.length, r, c]);

  // Stadium: grow wanted as sentinels intersect the map viewport.
  useEffect(() => {
    if (!viewportLoad || loading || !list.length || r < 1 || c < 1) return undefined;
    const root = canvasRef.current?.parentElement;
    if (!root) return undefined;

    let alive = true;
    const io = new IntersectionObserver(
      (entries) => {
        if (!alive) return;
        const hit = [];
        entries.forEach((entry) => {
          const key = entry.target.dataset.loadKey;
          if (!key || !entry.isIntersecting) return;
          hit.push(key);
        });
        if (!hit.length) return;
        setWanted((prev) => {
          let changed = false;
          const next = new Set(prev);
          hit.forEach((key) => {
            if (!next.has(key)) {
              next.add(key);
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      },
      { root: viewportEl || null, rootMargin: '30%', threshold: 0 },
    );

    root.querySelectorAll('[data-load-key]').forEach((el) => io.observe(el));
    return () => {
      alive = false;
      io.disconnect();
    };
  }, [viewportLoad, loading, scanKey, list.length, r, c, viewportEl]);

  // Decode wanted tiles. Only scanKey (genRef) invalidates — never abort on re-render.
  useEffect(() => {
    if (loading || !wanted.size) return;
    const gen = genRef.current;

    wanted.forEach((key) => {
      if (imagesRef.current.has(key) || inflightRef.current.has(key)) return;
      const tile = listRef.current.find((t) => tileKeyOf(t) === key);
      if (!tile) return;
      inflightRef.current.add(key);
      loadOneTile(tile).then((img) => {
        inflightRef.current.delete(key);
        if (gen !== genRef.current) return;
        imagesRef.current.set(key, img);
        setTick((n) => n + 1);
      }).catch(() => {
        inflightRef.current.delete(key);
        if (gen !== genRef.current) return;
        // Leave key absent so a later wanted pass can retry (null would block forever).
        setTick((n) => n + 1);
      });
    });
  }, [wanted, loading, scanKey]);

  // Base layer: tiles ONLY. Label/box toggles must never redraw images.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || loading || r < 1 || c < 1) return undefined;
    let alive = true;

    const imgs = imagesRef.current;
    const { tw, th } = sourceCellSize(imgs, list);
    const nativeW = c * tw;
    const nativeH = r * th;
    const fit = Math.min(1, MAX_CANVAS_EDGE / nativeW, MAX_CANVAS_EDGE / nativeH);
    const w = Math.max(1, Math.floor(nativeW * fit));
    const h = Math.max(1, Math.floor(nativeH * fit));
    const cellW = w / c;
    const cellH = h / r;
    const prev = atlasRef.current;
    const sizeChanged = prev.w !== w || prev.h !== h;
    atlasRef.current = {
      w, h, cellW, cellH,
    };

    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }

    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return undefined;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#1a1d22';
    ctx.fillRect(0, 0, w, h);

    list.forEach((tile) => {
      const key = tileKeyOf(tile);
      const img = imgs.get(key);
      const dx = (Number(tile.col) || 0) * cellW;
      const dy = (Number(tile.row) || 0) * cellH;
      if (!img) {
        ctx.fillStyle = '#2a2e35';
        ctx.fillRect(dx, dy, cellW, cellH);
        return;
      }
      ctx.drawImage(img, dx, dy, cellW, cellH);
    });

    if (sizeChanged && alive) setAtlasEpoch((n) => n + 1);
    return () => { alive = false; };
  }, [tick, loading, list, r, c]);

  // Boxes layer: selection change / detections — never redraws mosaic images.
  // Visibility is CSS [data-boxes-show]; this always keeps the bitmap ready.
  useEffect(() => {
    const canvas = boxesCanvasRef.current;
    if (!canvas || loading || r < 1 || c < 1) return;
    const {
      w, h, cellW, cellH,
    } = ensureAtlasSize();

    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, w, h);
    if (!selectedLabels?.size) return;
    paintDetectionFrames(
      ctx,
      list,
      cellW,
      cellH,
      tileBoxes,
      selectedLabels,
      boxKeyPrefix,
      imagesRef.current,
    );
  }, [
    loading, list, r, c, tileBoxes, selectedLabels, boxKeyPrefix, atlasEpoch, tick,
  ]);

  // SRS layer: bake once when overlay / atlas size ready. Toggle does not touch this.
  useEffect(() => {
    const canvas = srsCanvasRef.current;
    if (!canvas || loading || r < 1 || c < 1) return;
    const {
      w, h, cellW, cellH,
    } = ensureAtlasSize();

    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    paintSrsLayer(ctx, w, h, cellW, cellH, overlay, scanId);
  }, [loading, overlay, scanId, r, c, atlasEpoch]);

  const hasAnyImage = imagesRef.current.size > 0;
  const showLoading = loading || (!hasAnyImage && list.length > 0);

  return (
    <div className={style.mosaicLayer} aria-hidden={false}>
      <canvas
        ref={canvasRef}
        className={style.mosaicCanvas}
      />
      <canvas
        ref={boxesCanvasRef}
        className={style.boxesCanvas}
        aria-hidden
      />
      <canvas
        ref={srsCanvasRef}
        className={style.srsCanvas}
        aria-hidden
      />
      {viewportLoad && !loading && list.length > 0 ? (
        <div
          className={style.loadSentinels}
          style={{
            gridTemplateColumns: `repeat(${c}, ${TILE_CSS_W}px)`,
            gridTemplateRows: `repeat(${r}, ${TILE_CSS_H}px)`,
          }}
        >
          {list.map((tile) => {
            const key = tileKeyOf(tile);
            return (
              <div
                key={boxKeyPrefix ? `${boxKeyPrefix}:${key}` : key}
                data-load-key={key}
                className={style.loadSentinel}
              />
            );
          })}
        </div>
      ) : null}
      {showLoading ? <div className={style.mosaicLoading} /> : null}
    </div>
  );
};

export default memo(MosaicCanvas);
